import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { carregarFilaPorSenha } from '@/lib/presencial';
import DicaPrimeiraVez from '@/components/feature/DicaPrimeiraVez';
import TabelaInscritos from '../components/TabelaInscritos';
import CartaoInscritoMobile from '../components/CartaoInscritoMobile';
import ListaCompradores from '../components/ListaCompradores';
import PainelInscricao from '../components/PainelInscricao';
import AbaFiltrar from '../components/AbaFiltrar';
import AbaRelatorios from '../components/AbaRelatorios';
import type { SituacaoCamiseta } from '../components/RelatorioCamisetas';
import FolhaConvite from '../components/FolhaConvite';
import PainelVagas from '../components/PainelVagas';
import ListaSuplentes from '../components/ListaSuplentes';
import Sheet from '@/components/ui/Sheet';
import { motion } from 'motion/react';
import Aviso from '@/components/movimento/Aviso';
import TrocaFade from '@/components/movimento/TrocaFade';
import NumeroAnimado from '@/components/movimento/NumeroAnimado';
import LinhaQueSai from './LinhaQueSai';
import { chaveGrupo, chaveSenha, nomeDoGrupo, ordinal } from '../vagas';
import type { AvisoVagas, ContextoVagas } from '../vagas';
import {
  baixarCSV,
  STATUS_LABEL,
  situacaoComprador,
  formatarDinheiro,
  formatarDataCurta,
  formatarDataMinuto,
  montarParticipacoes,
} from '../utils';
import type { StatusInscricao } from '@/components/feature/SeloSituacao';
import type {
  FilaLinha,
  GrupoVagas,
  InfoLinhaVagas,
  InscritoGerenciar,
  LinhaParticipacao,
  Participacao,
  RespostaLinha,
  SuplenteLinha,
} from '../types';

const POR_PAGINA = 50;

// Cada aba da lista é um filtro de situação de verdade (vai na consulta).
const STATUS_POR_ABA: Record<'vaga' | 'suplente' | 'cancelada', StatusInscricao[]> = {
  vaga: ['pendente', 'confirmada', 'isenta'],
  suplente: ['lista_espera'],
  cancelada: ['cancelada'],
};

interface Props {
  eventoId: number;
  eventoNome: string;
  eventoCapa?: string | null;
  eventoLocal?: string | null;
  vendeIngressos: boolean;
  mostrarFicha: boolean;
  rotuloParticipacoes: string;
  usuarioId: string;
  eventoInicio: string;
  /** Fixa a lista num público do acampamento: só equipe ou só campistas. */
  papel?: 'equipe' | 'campista' | null;
  /** Prefixo do resumo do filtro ("Equipe", "Campistas"). */
  rotuloPublico?: string;
  /** Avisa a aba mãe qual lista está na tela (para os relatórios). */
  aoMudarFiltro?: (filtro: { ids: number[]; resumo: string }) => void;
  /** Esconde o painel de vagas (a tela cheia já tem o dela) e mostra só uma linha por grupo. */
  ocultarPainelVagas?: boolean;
  /** Leva para a tela cheia de "Vagas e suplentes". */
  aoAbrirVagas?: () => void;
}

// Remove acentos e deixa minúsculo, para busca local sem depender de acento/maiúscula.
function semAcentos(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Número da senha ("M07" → 7). Sem senha vai para o fim da fila.
function numeroSenha(senha: string | null | undefined): number {
  const n = Number(String(senha ?? '').replace(/\D/g, ''));
  return Number.isFinite(n) && n > 0 ? n : Number.MAX_SAFE_INTEGER;
}

// Célula-resumo de um grupo de vagas (grade 2×2, compacta, acima da lista).
function ResumoGrupo({ g, aoTocar }: { g: GrupoVagas; aoTocar?: () => void }) {
  const alerta = g.alerta_vaga_livre;
  const corpo = (
    <>
      <span className="block truncate text-[12px] font-bold leading-tight text-foreground-950">
        {nomeDoGrupo(g)}
      </span>
      <span
        className={`mt-0.5 block truncate text-[11px] leading-tight ${
          alerta ? 'font-semibold text-amber-800' : 'text-foreground-600'
        }`}
      >
        {g.com_vaga}/{g.vagas} · {g.suplentes}{' '}
        {g.suplentes === 1 ? 'suplente' : 'suplentes'}
      </span>
    </>
  );
  const classe = `flex min-h-[56px] min-w-0 w-full flex-col justify-center rounded-2xl border px-2.5 py-2 text-left ${
    alerta ? 'border-amber-300 bg-amber-50' : 'border-background-200 bg-white'
  }`;
  if (!aoTocar) return <div className={classe}>{corpo}</div>;
  return (
    <button
      type="button"
      onClick={aoTocar}
      className={`${classe} cursor-pointer transition-colors ${
        alerta ? 'hover:bg-amber-100' : 'hover:bg-background-100'
      }`}
    >
      {corpo}
    </button>
  );
}

// Linha da folha "Ações" da lista.
function LinhaAcao({
  icone,
  titulo,
  descricao,
  ativo = false,
  desabilitado = false,
  aoTocar,
}: {
  icone: string;
  titulo: string;
  descricao: string;
  ativo?: boolean;
  desabilitado?: boolean;
  aoTocar: () => void;
}) {
  return (
    <button
      type="button"
      onClick={aoTocar}
      disabled={desabilitado}
      className={`flex min-h-[58px] w-full cursor-pointer items-center gap-3 rounded-2xl px-3.5 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
        ativo ? 'bg-primary-50' : 'hover:bg-background-100'
      }`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-lg ${
          ativo ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-900'
        }`}
      >
        <i className={icone} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-semibold text-foreground-950">
          {titulo}
        </span>
        <span className="mt-0.5 block truncate text-[12px] text-foreground-600">
          {descricao}
        </span>
      </span>
      {ativo && (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
          <i className="ri-check-line" aria-hidden />
        </span>
      )}
    </button>
  );
}

export default function AbaPessoas({
  eventoId,
  eventoNome,
  eventoCapa,
  eventoLocal,
  vendeIngressos,
  mostrarFicha,
  rotuloParticipacoes,
  usuarioId,
  eventoInicio,
  papel = null,
  rotuloPublico,
  aoMudarFiltro,
  ocultarPainelVagas = false,
  aoAbrirVagas,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [inscritos, setInscritos] = useState<InscritoGerenciar[]>([]);
  const [saldoMap, setSaldoMap] = useState<Map<number, number>>(new Map());
  const [temFoto, setTemFoto] = useState(false);
  const [fotoUrlMap, setFotoUrlMap] = useState<Map<number, string>>(new Map());
  const [participacoes, setParticipacoes] = useState<Participacao[]>([]);
  const [reload, setReload] = useState(0);
  const primeiraCarga = useRef(true);
  const [equipeMap, setEquipeMap] = useState<Map<number, string>>(new Map());
  const [filaMap, setFilaMap] = useState<Map<number, FilaLinha>>(new Map());
  const [modalidadesDoPapel, setModalidadesDoPapel] = useState<number[]>([]);

  const [busca, setBusca] = useState('');
  // Evento sem vagas: um seletor só, Ativas · Canceladas.
  const [abaSimples, setAbaSimples] = useState<'ativas' | 'cancelada'>(() =>
    searchParams.get('situacao') === 'cancelada' ? 'cancelada' : 'ativas',
  );
  // Atalhos que moraram nos chips antigos e agora ficam na folha "Ações".
  // ?situacao= é o que o Início usa para abrir já filtrado (ex.: "falta pagar").
  const [filtroRapido, setFiltroRapido] = useState<'todos' | 'pagos' | 'falta'>(() => {
    const s = searchParams.get('situacao');
    if (s === 'pendente' || s === 'falta') return 'falta';
    if (s === 'confirmada' || s === 'pagos') return 'pagos';
    return 'todos';
  });
  const [acoesAbertas, setAcoesAbertas] = useState(false);
  const [modoSelecao, setModoSelecao] = useState(false);
  const [participacao, setParticipacao] = useState<string>('todas');
  const [pagina, setPagina] = useState(1);
  const [baixando, setBaixando] = useState(false);
  const [selecionado, setSelecionado] = useState<InscritoGerenciar | null>(null);
  const [convite, setConvite] = useState<InscritoGerenciar | null>(null);

  const [modalFiltro, setModalFiltro] = useState(false);
  const [modalRelatorio, setModalRelatorio] = useState(false);

  // Vagas e suplentes
  const [gruposVagas, setGruposVagas] = useState<GrupoVagas[]>([]);
  const [filaSuplentes, setFilaSuplentes] = useState<SuplenteLinha[]>([]);
  const [detalheMap, setDetalheMap] = useState<Map<number, Partial<InscritoGerenciar>>>(new Map());
  const [abaLista, setAbaLista] = useState<'vaga' | 'suplente' | 'cancelada'>(() => {
    const s = searchParams.get('situacao');
    if (s === 'cancelada') return 'cancelada';
    if (s === 'lista_espera' || s === 'suplente') return 'suplente';
    return 'vaga';
  });
  // Linhas só da aba atual, vindas de uma consulta já filtrada pelo status.
  const [inscritosAba, setInscritosAba] = useState<InscritoGerenciar[] | null>(null);
  const [aviso, setAviso] = useState<AvisoVagas | null>(null);
  const [acaoVagas, setAcaoVagas] = useState(false);
  // Linha que sai encolhendo antes da releitura (cancelar / dar vaga / suplente).
  const [saindoId, setSaindoId] = useState<number | null>(null);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      if (primeiraCarga.current) setCarregando(true);
      setErro(null);
      try {
        const [resInscritos, resSaldos, resPart, resMods] = await Promise.all([
          supabase
            .from('vw_inscritos')
            .select(
              'inscricao_id, codigo, modalidade_id, modalidade, grupo, participante_id, participante, participante_telefone, participante_email, cpf, nascimento, responsavel_id, responsavel, responsavel_email, responsavel_telefone, valor_centavos, status, criado_em',
            )
            .eq('evento_id', eventoId),
          supabase
            .from('vw_saldo_inscricao')
            .select('inscricao_id, saldo_centavos')
            .eq('evento_id', eventoId),
          supabase.from('vw_participacoes').select('*').eq('evento_id', eventoId),
          supabase.from('modalidades').select('id, papel').eq('evento_id', eventoId),
        ]);
        if (resInscritos.error) throw resInscritos.error;
        if (resSaldos.error) throw resSaldos.error;
        if (resPart.error) throw resPart.error;
        if (!ativo) return;

        const mods = (resMods.data as { id: number; papel: string | null }[]) || [];
        const idsDoPapel = new Set(
          mods.filter((m) => (papel ? m.papel === papel : true)).map((m) => Number(m.id)),
        );
        setModalidadesDoPapel(papel ? [...idsDoPapel] : []);

        const lista = (((resInscritos.data as InscritoGerenciar[]) || []).filter((i) =>
          papel ? idsDoPapel.has(Number(i.modalidade_id)) : true,
        ) as InscritoGerenciar[]).sort(
          (a, b) => new Date(b.criado_em).getTime() - new Date(a.criado_em).getTime(),
        );
        const saldo = new Map<number, number>();
        ((resSaldos.data as { inscricao_id: number; saldo_centavos: number }[]) || []).forEach(
          (linha) => saldo.set(linha.inscricao_id, linha.saldo_centavos),
        );
        setInscritos(lista);
        setSaldoMap(saldo);
        setParticipacoes(
          montarParticipacoes((resPart.data as LinhaParticipacao[]) || []).filter((p) =>
            papel ? idsDoPapel.has(Number(p.modalidade_id)) : true,
          ),
        );
        setCarregando(false);
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar a lista.'));
          setCarregando(false);
        }
      } finally {
        if (ativo) primeiraCarga.current = false;
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [eventoId, reload, papel]);

  // Equipe de serviço de cada pessoa (só na aba Equipe).
  useEffect(() => {
    if (papel !== 'equipe' || inscritos.length === 0) {
      setEquipeMap(new Map());
      return;
    }
    let ativo = true;
    (async () => {
      try {
        const { data } = await supabase
          .from('vw_montagem')
          .select('inscricao_id, equipe')
          .eq('evento_id', eventoId);
        if (!ativo) return;
        const mapa = new Map<number, string>();
        ((data as { inscricao_id: number; equipe: string | null }[]) || []).forEach((l) => {
          if (l.equipe) mapa.set(Number(l.inscricao_id), l.equipe);
        });
        setEquipeMap(mapa);
      } catch {
        /* sem equipe: a coluna fica vazia */
      }
    })();
    return () => {
      ativo = false;
    };
  }, [papel, eventoId, inscritos.length]);

  // Fila pela senha (senha, vaga/suplente) dos campistas.
  useEffect(() => {
    if (papel !== 'campista' || modalidadesDoPapel.length === 0) {
      setFilaMap(new Map());
      return;
    }
    let ativo = true;
    (async () => {
      const mapa = new Map<number, FilaLinha>();
      for (const modalidadeId of modalidadesDoPapel) {
        try {
          const fila = await carregarFilaPorSenha(modalidadeId);
          fila.grupos.forEach((g) => {
            g.itens.forEach((it) => {
              mapa.set(Number(it.inscricao_id), {
                senha: it.senha,
                situacao: it.situacao,
                falta: it.falta,
              });
            });
          });
        } catch {
          /* modalidade sem senha: segue sem a coluna */
        }
      }
      if (ativo) setFilaMap(mapa);
    })();
    return () => {
      ativo = false;
    };
  }, [papel, modalidadesDoPapel]);

  // Painel de vagas, fila de suplentes e campos de cancelamento/subida.
  useEffect(() => {
    if (Number.isNaN(eventoId)) return;
    let ativo = true;
    (async () => {
      const [resPainel, resFila, resDetalhe] = await Promise.all([
        supabase.rpc('painel_vagas', { p_evento: eventoId }),
        supabase.rpc('fila_de_suplentes', { p_evento: eventoId }),
        supabase
          .from('inscricoes')
          .select(
            'id, opcao_id, senha_fila, senha_num, validada_em, subiu_em, subiu_por, subiu_motivo, subiu_no_lugar_de, cancelada_em, cancelamento_motivo, cancelamento_detalhe',
          )
          .eq('evento_id', eventoId),
      ]);
      if (!ativo) return;
      setGruposVagas(
        !resPainel.error && Array.isArray(resPainel.data) ? (resPainel.data as GrupoVagas[]) : [],
      );
      setFilaSuplentes(
        !resFila.error && Array.isArray(resFila.data) ? (resFila.data as SuplenteLinha[]) : [],
      );
      const mapa = new Map<number, Partial<InscritoGerenciar>>();
      ((resDetalhe.data as Record<string, unknown>[]) || []).forEach((l) => {
        const id = Number(l.id);
        mapa.set(id, {
          opcao_id: l.opcao_id === null ? null : Number(l.opcao_id),
          senha_fila: (l.senha_fila as string | null) ?? null,
          senha_num: l.senha_num === null ? null : Number(l.senha_num),
          validada_em: (l.validada_em as string | null) ?? null,
          subiu_em: (l.subiu_em as string | null) ?? null,
          subiu_por: (l.subiu_por as string | null) ?? null,
          subiu_motivo: (l.subiu_motivo as string | null) ?? null,
          subiu_no_lugar_de: l.subiu_no_lugar_de === null ? null : Number(l.subiu_no_lugar_de),
          cancelada_em: (l.cancelada_em as string | null) ?? null,
          cancelamento_motivo: (l.cancelamento_motivo as string | null) ?? null,
          cancelamento_detalhe: (l.cancelamento_detalhe as string | null) ?? null,
        });
      });
      setDetalheMap(mapa);
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId, reload]);

  // Mantém a folha aberta em dia depois de cada ação.
  useEffect(() => {
    if (!selecionado) return;
    const novo = inscritos.find((i) => i.inscricao_id === selecionado.inscricao_id);
    if (novo && novo !== selecionado) setSelecionado(novo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inscritos]);

  // Tempo real: quando alguém compra (linha nova em `inscricoes`), a lista se atualiza.
  useEffect(() => {
    if (Number.isNaN(eventoId)) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const canal = supabase
      .channel(`inscritos-${eventoId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'inscricoes',
          filter: `evento_id=eq.${eventoId}`,
        },
        () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => setReload((r) => r + 1), 500);
        },
      )
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(canal);
    };
  }, [eventoId]);

  // Fotos de identificação.
  useEffect(() => {
    let ativo = true;
    if (inscritos.length === 0) {
      setTemFoto(false);
      setFotoUrlMap(new Map());
      return;
    }
    (async () => {
      try {
        const { data: modsData } = await supabase
          .from('modalidades')
          .select('id')
          .eq('evento_id', eventoId);
        const modIds = ((modsData as { id: number }[]) || []).map((m) => m.id);
        let tem = false;
        if (modIds.length > 0) {
          const { data: formsData } = await supabase
            .from('formularios')
            .select('id')
            .in('modalidade_id', modIds)
            .eq('ativo', true);
          const formIds = ((formsData as { id: number }[]) || []).map((f) => f.id);
          if (formIds.length > 0) {
            const { data: perguntasFoto } = await supabase
              .from('perguntas')
              .select('id')
              .in('formulario_id', formIds)
              .eq('tipo', 'foto');
            tem = ((perguntasFoto as unknown[]) || []).length > 0;
          }
        }
        if (!ativo) return;
        setTemFoto(tem);
        if (!tem) return;
        const ids = inscritos.map((i) => i.inscricao_id);
        const { data: respData } = await supabase
          .from('vw_respostas_inscricao')
          .select('inscricao_id, resposta_texto')
          .eq('tipo', 'foto')
          .in('inscricao_id', ids);
        const linhas =
          (respData as { inscricao_id: number; resposta_texto: string | null }[]) || [];
        const caminhos = [
          ...new Set(linhas.filter((r) => r.resposta_texto).map((r) => r.resposta_texto as string)),
        ];
        const urlPorCaminho = new Map<string, string>();
        if (caminhos.length > 0) {
          const { data: signed } = await supabase.storage
            .from('fichas')
            .createSignedUrls(caminhos, 3600);
          (signed || []).forEach((s) => {
            if (s.signedUrl) urlPorCaminho.set(s.path, s.signedUrl);
          });
        }
        const mapa = new Map<number, string>();
        linhas.forEach((r) => {
          if (!mapa.has(r.inscricao_id) && r.resposta_texto && urlPorCaminho.has(r.resposta_texto)) {
            mapa.set(r.inscricao_id, urlPorCaminho.get(r.resposta_texto) as string);
          }
        });
        if (ativo) setFotoUrlMap(mapa);
      } catch {
        /* falha silenciosa */
      }
    })();
    return () => {
      ativo = false;
    };
  }, [inscritos, eventoId]);

  const participacaoAtiva = useMemo(() => {
    if (participacao === 'todas') return null;
    return participacoes.find((p) => String(p.modalidade_id) === participacao) ?? null;
  }, [participacoes, participacao]);

  const gruposVisiveis = useMemo(() => {
    if (papel && modalidadesDoPapel.length > 0) {
      const ids = new Set(modalidadesDoPapel.map(Number));
      return gruposVagas.filter((g) => ids.has(Number(g.modalidade_id)));
    }
    return gruposVagas;
  }, [gruposVagas, modalidadesDoPapel, papel]);

  const gruposComVagas = useMemo(
    () => gruposVisiveis.filter((g) => g.vagas !== null && g.vagas !== undefined),
    [gruposVisiveis],
  );
  const temVagas = gruposComVagas.length > 0;

  const posicaoMap = useMemo(() => {
    const m = new Map<number, number>();
    filaSuplentes.forEach((f) => m.set(Number(f.inscricao_id), f.posicao));
    return m;
  }, [filaSuplentes]);

  // Ordem dos grupos que aparecem na fila (o grupo da posição 1 vem primeiro).
  const ordemGrupo = useMemo(() => {
    const m = new Map<string, number>();
    [...filaSuplentes]
      .sort((a, b) => a.posicao - b.posicao)
      .forEach((f) => {
        const chave = chaveGrupo(f.modalidade_id, f.opcao_id);
        if (!m.has(chave)) m.set(chave, m.size);
      });
    return m;
  }, [filaSuplentes]);

  // A aba escolhida define o filtro de situação da consulta: cada troca busca de novo.
  useEffect(() => {
    if (vendeIngressos || !temVagas) {
      setInscritosAba(null);
      return;
    }
    if (papel && modalidadesDoPapel.length === 0) return;
    // Limpa antes de buscar: a lista cai no filtro local (mesmo resultado) e nao pisca vazia.
    setInscritosAba(null);
    let ativo = true;
    (async () => {
      try {
        let consulta = supabase
          .from('vw_inscritos')
          .select(
            'inscricao_id, codigo, modalidade_id, modalidade, grupo, participante_id, participante, participante_telefone, participante_email, cpf, nascimento, responsavel_id, responsavel, responsavel_email, responsavel_telefone, valor_centavos, status, criado_em',
          )
          .eq('evento_id', eventoId)
          .in('status', STATUS_POR_ABA[abaLista]);
        if (papel) consulta = consulta.in('modalidade_id', modalidadesDoPapel);
        const { data, error } = await consulta;
        if (error) throw error;
        if (!ativo) return;
        setInscritosAba(
          ((data as InscritoGerenciar[]) || []).sort(
            (a, b) => new Date(b.criado_em).getTime() - new Date(a.criado_em).getTime(),
          ),
        );
      } catch {
        // Se a consulta da aba falhar, a lista filtrada local continua valendo.
        if (ativo) setInscritosAba(null);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId, abaLista, temVagas, vendeIngressos, papel, modalidadesDoPapel, reload]);

  const infoVagas = useMemo(() => {
    const m = new Map<number, InfoLinhaVagas>();
    detalheMap.forEach((d, id) => {
      const f = filaSuplentes.find((x) => Number(x.inscricao_id) === id);
      m.set(id, {
        posicao: posicaoMap.get(id) ?? null,
        senha: f?.senha ?? d.senha_fila ?? null,
        validada: f?.validada ?? !!d.validada_em,
        subiu_em: d.subiu_em ?? null,
        cancelada_em: d.cancelada_em ?? null,
        cancelamento_motivo: d.cancelamento_motivo ?? null,
        cancelamento_detalhe: d.cancelamento_detalhe ?? null,
      });
    });
    return m;
  }, [detalheMap, filaSuplentes, posicaoMap]);

  const contagemAbas = useMemo(() => {
    let vaga = 0;
    let suplente = 0;
    let cancelada = 0;
    inscritos.forEach((i) => {
      if (participacaoAtiva && i.modalidade_id !== participacaoAtiva.modalidade_id) return;
      if (i.status === 'cancelada') cancelada += 1;
      else if (i.status === 'lista_espera') suplente += 1;
      else vaga += 1;
    });
    return { vaga, suplente, cancelada };
  }, [inscritos, participacaoAtiva]);

  const contagemSenhas = useMemo(() => {
    const m = new Map<string, number>();
    inscritos.forEach((i) => {
      const d = detalheMap.get(i.inscricao_id);
      if (d?.senha_num === null || d?.senha_num === undefined) return;
      const chave = chaveSenha(
        chaveGrupo(i.modalidade_id, d.opcao_id ?? null),
        d.senha_num as number,
      );
      m.set(chave, (m.get(chave) ?? 0) + 1);
    });
    return m;
  }, [detalheMap, inscritos]);

  const telefonePorInscricao = useMemo(() => {
    const m = new Map<number, string>();
    inscritos.forEach((i) => {
      if (i.participante_telefone) m.set(i.inscricao_id, i.participante_telefone);
    });
    return m;
  }, [inscritos]);

  const filtrados = useMemo(() => {
    const q = semAcentos(busca.trim());
    const qDigitos = q.replace(/\D/g, '');
    // Com vagas, a lista vem da consulta da aba (já filtrada por situação).
    const base = !vendeIngressos && temVagas && inscritosAba !== null ? inscritosAba : inscritos;
    const lista = base.filter((i) => {
      if (participacaoAtiva && i.modalidade_id !== participacaoAtiva.modalidade_id) return false;
      if (temVagas) {
        // Trava de segurança: enquanto a consulta da aba não volta, nenhuma pessoa de outra situação entra.
        if (abaLista === 'cancelada' && i.status !== 'cancelada') return false;
        if (abaLista === 'suplente' && i.status !== 'lista_espera') return false;
        if (
          abaLista === 'vaga' &&
          (i.status === 'lista_espera' || i.status === 'cancelada')
        ) {
          return false;
        }
      } else if (abaSimples === 'cancelada') {
        if (i.status !== 'cancelada') return false;
      } else if (i.status === 'cancelada') {
        return false;
      }
      if (filtroRapido === 'pagos' && i.status !== 'confirmada') return false;
      if (filtroRapido === 'falta') {
        if (i.status === 'cancelada') return false;
        if (vendeIngressos) {
          if (i.status !== 'pendente') return false;
        } else if ((saldoMap.get(i.inscricao_id) ?? 0) <= 0) return false;
      }
      if (!q) return true;
      if (semAcentos(i.participante).includes(q)) return true;
      if (qDigitos) {
        if (vendeIngressos) {
          const tel = (i.participante_telefone ?? '').replace(/\D/g, '');
          if (tel.includes(qDigitos)) return true;
        } else {
          const cpf = (i.cpf ?? '').replace(/\D/g, '');
          if (cpf.includes(qDigitos)) return true;
        }
      }
      return false;
    });

    // Suplentes: por grupo (ordem da fila) e, dentro do grupo, na posição exata.
    // Quem ainda não está validado (sem posição) cai no fim.
    if (temVagas && abaLista === 'suplente') {
      const grupoDe = (i: InscritoGerenciar) =>
        chaveGrupo(i.modalidade_id, detalheMap.get(i.inscricao_id)?.opcao_id ?? null);
      return [...lista].sort((a, b) => {
        const ga = ordemGrupo.get(grupoDe(a)) ?? Number.MAX_SAFE_INTEGER;
        const gb = ordemGrupo.get(grupoDe(b)) ?? Number.MAX_SAFE_INTEGER;
        if (ga !== gb) return ga - gb;
        return (
          (posicaoMap.get(a.inscricao_id) ?? Number.MAX_SAFE_INTEGER) -
          (posicaoMap.get(b.inscricao_id) ?? Number.MAX_SAFE_INTEGER)
        );
      });
    }

    // Campistas: primeiro quem tem vaga (grupo e senha crescente), depois os suplentes.
    if (papel !== 'campista') return lista;
    return [...lista].sort((a, b) => {
      const fa = filaMap.get(a.inscricao_id);
      const fb = filaMap.get(b.inscricao_id);
      const suplenteA = a.status === 'lista_espera' || fa?.situacao === 'suplente' ? 1 : 0;
      const suplenteB = b.status === 'lista_espera' || fb?.situacao === 'suplente' ? 1 : 0;
      if (suplenteA !== suplenteB) return suplenteA - suplenteB;
      const grupoA = a.grupo ?? '';
      const grupoB = b.grupo ?? '';
      if (grupoA !== grupoB) return grupoA.localeCompare(grupoB, 'pt-BR');
      const senhaA = numeroSenha(fa?.senha);
      const senhaB = numeroSenha(fb?.senha);
      if (senhaA !== senhaB) return senhaA - senhaB;
      return new Date(a.criado_em).getTime() - new Date(b.criado_em).getTime();
    });
  }, [
    inscritos,
    inscritosAba,
    busca,
    participacaoAtiva,
    abaSimples,
    filtroRapido,
    saldoMap,
    vendeIngressos,
    papel,
    filaMap,
    temVagas,
    abaLista,
    posicaoMap,
    ordemGrupo,
    detalheMap,
  ]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const inicio = (paginaSegura - 1) * POR_PAGINA;
  const paginados = filtrados.slice(inicio, inicio + POR_PAGINA);

  // Quantos suplentes cada grupo tem de verdade (não só os da página).
  const totaisPorGrupo = useMemo(() => {
    const m = new Map<string, number>();
    filtrados.forEach((i) => {
      const chave = chaveGrupo(i.modalidade_id, detalheMap.get(i.inscricao_id)?.opcao_id ?? null);
      m.set(chave, (m.get(chave) ?? 0) + 1);
    });
    return m;
  }, [filtrados, detalheMap]);

  // ————— Seleção em lote e impressão —————
  const idsDaAba = useMemo(() => filtrados.map((i) => i.inscricao_id), [filtrados]);
  const todosMarcados =
    idsDaAba.length > 0 && idsDaAba.every((id) => selecionados.has(id));

  // A linha encolhe por 200 ms e só então a lista é relida.
  function agendarSaida(inscricaoId: number | null) {
    if (!inscricaoId) {
      setReload((r) => r + 1);
      return;
    }
    setSaindoId(inscricaoId);
    window.setTimeout(() => {
      setSaindoId(null);
      setReload((r) => r + 1);
    }, 210);
  }

  function alternarSelecao(inscricaoId: number) {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(inscricaoId)) novo.delete(inscricaoId);
      else novo.add(inscricaoId);
      return novo;
    });
  }

  function marcarTodasDaAba() {
    setSelecionados(todosMarcados ? new Set() : new Set(idsDaAba));
  }

  function imprimirFichasSelecionadas() {
    if (selecionados.size === 0) return;
    const visiveis = new Set(idsDaAba);
    const ids = [...selecionados].filter((id) => visiveis.has(id));
    if (ids.length === 0) return;
    navigate(`/gerenciar/${eventoId}/fichas/imprimir?ids=${ids.join(',')}`);
  }

  function imprimirLista() {
    if (filtrados.length === 0) return;
    navigate(
      `/gerenciar/${eventoId}/lista/imprimir?ids=${idsDaAba.join(',')}&titulo=${encodeURIComponent(
        resumoFiltro,
      )}`,
    );
  }

  // ————— Filtro atual: tudo o que sai daqui (relatórios, folhas, planilhas) —————
  const mostrarAbasParticipacao = participacoes.length > 1;
  void acaoVagas;
  const temFiltroAtivo =
    participacao !== 'todas' ||
    (temVagas ? abaLista !== 'vaga' : abaSimples !== 'ativas') ||
    filtroRapido !== 'todos' ||
    busca.trim().length > 0;
  const rotuloAba =
    abaLista === 'suplente' ? 'suplentes' : abaLista === 'cancelada' ? 'canceladas' : 'com vaga';

  // Inscrições da lista filtrada inteira (todas as páginas). null = evento inteiro.
  const inscricaoIdsFiltrados = useMemo(
    () => filtrados.map((i) => i.inscricao_id),
    [filtrados],
  );

  // Só a participação escolhida (sem situação/busca), para o "Filtrar pela ficha".
  // Na aba Equipe/Campistas, o público inteiro (só as perguntas dele).
  const inscricaoIdsParticipacao = useMemo(() => {
    if (papel) return inscritos.map((i) => i.inscricao_id);
    if (!participacaoAtiva) return null;
    return inscritos
      .filter((i) => i.modalidade_id === participacaoAtiva.modalidade_id)
      .map((i) => i.inscricao_id);
  }, [inscritos, participacaoAtiva, papel]);

  const resumoFiltro = useMemo(() => {
    const n = filtrados.length;
    const pessoas = `${n} ${n === 1 ? 'pessoa' : 'pessoas'}`;
    const prefixo = rotuloPublico ? `${rotuloPublico} · ` : '';
    if (!temFiltroAtivo) {
      // Sem vagas, a lista de base já é "ativas" (canceladas ficam na outra aba).
      if (!temVagas) return `${prefixo}ativas · ${pessoas}`;
      if (rotuloPublico) return `${prefixo}${pessoas}`;
      return 'todos os inscritos';
    }
    const partes: string[] = [];
    if (temVagas && abaLista !== 'vaga') partes.push(rotuloAba);
    if (!temVagas && abaSimples === 'cancelada') partes.push('canceladas');
    if (participacaoAtiva) partes.push(participacaoAtiva.modalidade);
    if (filtroRapido === 'pagos') partes.push('confirmadas/pagas');
    if (filtroRapido === 'falta') partes.push('faltam pagar');
    if (busca.trim()) partes.push(`busca "${busca.trim()}"`);
    return `${prefixo}${partes.join(' · ')} · ${pessoas}`;
  }, [
    temFiltroAtivo,
    participacaoAtiva,
    abaSimples,
    filtroRapido,
    busca,
    filtrados.length,
    rotuloPublico,
    temVagas,
    abaLista,
    rotuloAba,
  ]);

  // Camisetas: a situação sai da aba da lista (com vaga / suplentes / canceladas).
  const situacaoCamisetas: SituacaoCamiseta = temVagas
    ? abaLista === 'suplente'
      ? 'suplentes'
      : abaLista === 'cancelada'
        ? 'todos'
        : 'com_vaga'
    : 'com_vaga';
  const rotuloSituacaoCamisetas = temVagas
    ? abaLista === 'suplente'
      ? 'Suplentes'
      : abaLista === 'cancelada'
        ? 'Canceladas'
        : 'Com vaga'
    : 'Com vaga';

  // A aba mãe usa esta lista (todas as páginas) nos relatórios.
  const chaveIdsFiltrados = useMemo(
    () => filtrados.map((i) => i.inscricao_id).join(','),
    [filtrados],
  );
  useEffect(() => {
    if (!aoMudarFiltro) return;
    aoMudarFiltro({
      ids: chaveIdsFiltrados ? chaveIdsFiltrados.split(',').map(Number) : [],
      resumo: resumoFiltro,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveIdsFiltrados, resumoFiltro]);

  function aoMudarParticipacao(v: string) {
    setParticipacao(v);
    setPagina(1);
  }

  async function baixarPlanilha() {
    if (filtrados.length === 0) return;
    setBaixando(true);
    try {
      const nomeDaAba = temVagas
        ? abaLista === 'vaga'
          ? 'com-vaga'
          : abaLista === 'suplente'
            ? 'suplentes'
            : 'canceladas'
        : abaSimples === 'cancelada'
          ? 'canceladas'
          : 'ativas';
      const sufixoGrupo = participacaoAtiva ? `-${participacaoAtiva.modalidade}` : '';
      const baseArquivo = `${nomeDaAba}${sufixoGrupo}-${eventoNome}`
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      if (vendeIngressos) {
        const cabecalho = ['Nome', 'Telefone', 'Ingresso', 'Situação', 'Data', 'Valor'];
        const linhas = filtrados.map((i) => [
          i.participante,
          i.participante_telefone ?? '',
          i.modalidade,
          situacaoComprador(i.status).rotulo,
          formatarDataMinuto(i.criado_em),
          formatarDinheiro(i.valor_centavos),
        ]);
        baixarCSV(`${baseArquivo}.csv`, [
          [`Filtro: ${resumoFiltro}`],
          [],
          cabecalho,
          ...linhas,
        ]);
        return;
      }

      const ids = filtrados.map((i) => i.inscricao_id);
      const { data: respostasData, error: errRespostas } = await supabase
        .from('vw_respostas_inscricao')
        .select('inscricao_id, ordem, enunciado, resposta_texto')
        .in('inscricao_id', ids);
      if (errRespostas) throw errRespostas;
      const respostas = (respostasData as RespostaLinha[]) || [];
      const ordemPergunta = new Map<string, number>();
      respostas.forEach((r) => {
        if (!ordemPergunta.has(r.enunciado)) ordemPergunta.set(r.enunciado, r.ordem);
      });
      const perguntas = [...ordemPergunta.entries()]
        .sort((a, b) => a[1] - b[1])
        .map(([enunciado]) => enunciado);
      const respostasPorInscricao = new Map<number, Map<string, string>>();
      respostas.forEach((r) => {
        if (!respostasPorInscricao.has(r.inscricao_id)) {
          respostasPorInscricao.set(r.inscricao_id, new Map());
        }
        respostasPorInscricao.get(r.inscricao_id)!.set(r.enunciado, r.resposta_texto ?? '');
      });

      const cidadeDe = (inscricaoId: number): string => {
        const resp = respostasPorInscricao.get(inscricaoId);
        if (!resp) return '';
        for (const [enunciado, valor] of resp.entries()) {
          if (enunciado.toLowerCase().includes('cidade') && valor) return valor;
        }
        return '';
      };

      const cabecalho = [
        'Nome',
        'Participação',
        'Grupo',
        'Situação',
        'Posição na fila',
        'Senha',
        'Telefone',
        'Cidade',
        'Pago/Falta',
        'Subiu em',
        'Cancelada em',
        'Motivo do cancelamento',
        ...perguntas,
      ];
      const linhas = filtrados.map((i) => {
        const saldo = saldoMap.get(i.inscricao_id) ?? null;
        const resp = respostasPorInscricao.get(i.inscricao_id);
        const det = detalheMap.get(i.inscricao_id);
        const info = infoVagas.get(i.inscricao_id);
        const posicao = posicaoMap.get(i.inscricao_id) ?? null;
        const senha = info?.senha ?? det?.senha_fila ?? null;
        const suplente = i.status === 'lista_espera';

        let pagoFalta = '';
        if (!suplente && i.status !== 'cancelada' && saldo !== null) {
          pagoFalta = saldo <= 0 ? 'Pago' : `Falta ${formatarDinheiro(saldo)}`;
        }

        let situacaoFicha: string;
        if (i.status === 'cancelada') situacaoFicha = 'Cancelada';
        else if (suplente) situacaoFicha = posicao ? `Suplente ${ordinal(posicao)}` : 'Suplente';
        else if (temVagas) situacaoFicha = 'Com vaga';
        else situacaoFicha = STATUS_LABEL[i.status];

        const motivo =
          i.status === 'cancelada'
            ? [info?.cancelamento_motivo ?? det?.cancelamento_motivo, info?.cancelamento_detalhe ?? det?.cancelamento_detalhe]
                .filter(Boolean)
                .join(' · ')
            : '';

        return [
          i.participante,
          i.modalidade,
          i.grupo ?? '',
          situacaoFicha,
          suplente && posicao ? String(posicao) : '',
          senha ?? '',
          i.participante_telefone ?? '',
          cidadeDe(i.inscricao_id),
          pagoFalta,
          i.status === 'cancelada' ? '' : (info?.subiu_em ?? det?.subiu_em ?? '') ? formatarDataCurta((info?.subiu_em ?? det?.subiu_em) as string) : '',
          i.status === 'cancelada'
            ? formatarDataCurta((info?.cancelada_em ?? det?.cancelada_em) as string)
            : '',
          motivo,
          ...perguntas.map((p) => resp?.get(p) ?? ''),
        ];
      });
      baixarCSV(`${baseArquivo}.csv`, [
        [`Filtro: ${resumoFiltro}`],
        [],
        cabecalho,
        ...linhas,
      ]);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível gerar a planilha.'));
    } finally {
      setBaixando(false);
    }
  }

  function aoMudouStatus(inscricaoId: number, status: StatusInscricao) {
    setInscritos((prev) => prev.map((i) => (i.inscricao_id === inscricaoId ? { ...i, status } : i)));
    setSelecionado((prev) =>
      prev && prev.inscricao_id === inscricaoId ? { ...prev, status } : prev,
    );
  }

  async function aoMudouFinanceiro(inscricaoId: number) {
    try {
      const { data } = await supabase
        .from('vw_saldo_inscricao')
        .select('inscricao_id, saldo_centavos')
        .eq('inscricao_id', inscricaoId)
        .maybeSingle();
      if (data) {
        setSaldoMap((prev) => new Map(prev).set(inscricaoId, (data as { saldo_centavos: number }).saldo_centavos));
      }
    } catch {
      /* silencioso */
    }
  }

  if (carregando) {
    return (
      <div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-9 w-24 shrink-0 esqueleto rounded-full bg-secondary-100" />
          ))}
        </div>
        <div className="mt-6 h-64 esqueleto rounded-lg bg-secondary-100" />
      </div>
    );
  }

  if (erro && inscritos.length === 0) {
    return (
      <div className="rounded-lg border border-secondary-200 bg-white p-10 text-center">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  const nPagos = inscritos.filter((i) => i.status === 'confirmada').length;
  const nAguardando = inscritos.filter((i) => i.status === 'pendente').length;
  const nNaoPagaram = inscritos.filter((i) => i.status === 'cancelada').length;

  const aoDarVagaDoPainel = async (inscricaoId: number, nome: string) => {
    setAcaoVagas(true);
    try {
      const { data } = await supabase.rpc('dar_vaga_ao_suplente', {
        p_inscricao: inscricaoId,
        p_motivo: null,
      });
      const res = data as { ok: boolean; erro?: string } | null;
      setAviso({
        texto: res?.ok
          ? `${nome} saiu dos suplentes e agora tem vaga.`
          : (res?.erro ?? 'Não foi possível dar a vaga.'),
      });
      if (res?.ok) agendarSaida(inscricaoId);
    } finally {
      setAcaoVagas(false);
    }
  };

  const aoAbrirPorId = (inscricaoId: number) => {
    const alvo = inscritos.find((i) => i.inscricao_id === inscricaoId);
    if (alvo) setSelecionado(alvo);
  };

  const contextoVagas = (i: InscritoGerenciar): ContextoVagas | undefined => {
    if (!temVagas) return undefined;
    const det = detalheMap.get(i.inscricao_id);
    const chave = chaveGrupo(i.modalidade_id, det?.opcao_id ?? null);
    const grupo = gruposVisiveis.find((g) => chaveGrupo(g.modalidade_id, g.opcao_id) === chave);
    const primeiro =
      filaSuplentes
        .filter((f) => chaveGrupo(f.modalidade_id, f.opcao_id) === chave)
        .sort((a, b) => a.posicao - b.posicao)[0] ?? null;
    const posicao = posicaoMap.get(i.inscricao_id) ?? null;
    return {
      temVagas: true,
      ehSuplente: i.status === 'lista_espera',
      temVaga: i.status !== 'lista_espera' && i.status !== 'cancelada',
      grupoNome: grupo?.grupo ?? grupo?.modalidade ?? null,
      primeiroSuplente:
        primeiro && primeiro.inscricao_id !== i.inscricao_id && (!posicao || posicao > 1)
          ? { inscricao_id: primeiro.inscricao_id, nome: primeiro.nome }
          : null,
    };
  };

  return (
    <div>
      {temVagas && !ocultarPainelVagas && (
        <PainelVagas
          grupos={gruposVisiveis}
          fila={filaSuplentes}
          telefonePorInscricao={telefonePorInscricao}
          contagemSenhas={contagemSenhas}
          aoDarVaga={aoDarVagaDoPainel}
          aoAbrirPessoa={aoAbrirPorId}
          ocupado={acaoVagas}
        />
      )}

      {/* Números (só ingressos) */}
      {vendeIngressos && (
        <p className="text-[13px] text-foreground-500">
          <strong className="font-semibold text-foreground-900">{nPagos}</strong> pagos ·{' '}
          <strong className="font-semibold text-foreground-900">{nAguardando}</strong> aguardando ·{' '}
          <strong className="font-semibold text-foreground-900">{nNaoPagaram}</strong> não pagaram
        </p>
      )}

      {/* Busca + Ações */}
      <div className={`flex items-center gap-2 ${vendeIngressos ? 'mt-1' : 'mt-3'}`}>
        <div className="relative min-w-0 flex-1">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
          <input
            type="text"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setPagina(1);
            }}
            placeholder={vendeIngressos ? 'Buscar por nome ou telefone' : 'Buscar por nome ou CPF'}
            className="h-11 w-full rounded-full border border-background-200 bg-white pl-11 pr-4 text-[15px] text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => setAcoesAbertas(true)}
          className="flex h-11 shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-200 bg-white px-3 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-more-fill text-base" aria-hidden />
          </span>
          Ações
        </button>
      </div>

      {/* Um seletor só: com vaga/suplentes/canceladas ou ativas/canceladas */}
      <div className="mt-3 flex gap-1 rounded-full bg-background-100 p-1">
        {(temVagas
          ? ([
              { valor: 'vaga', rotulo: 'Com vaga', contagem: contagemAbas.vaga },
              { valor: 'suplente', rotulo: 'Suplentes', contagem: contagemAbas.suplente },
              { valor: 'cancelada', rotulo: 'Canceladas', contagem: contagemAbas.cancelada },
            ] as const)
          : ([
              {
                valor: 'ativas',
                rotulo: 'Ativas',
                contagem: contagemAbas.vaga + contagemAbas.suplente,
              },
              { valor: 'cancelada', rotulo: 'Canceladas', contagem: contagemAbas.cancelada },
            ] as const)
        ).map((aba) => {
          const valor = aba.valor;
          const ativo = temVagas ? abaLista === valor : abaSimples === valor;
          return (
            <button
              key={valor}
              type="button"
              onClick={() => {
                if (ativo) return;
                if (temVagas) setAbaLista(valor as 'vaga' | 'suplente' | 'cancelada');
                else setAbaSimples(valor as 'ativas' | 'cancelada');
                setSelecionados(new Set());
                setPagina(1);
              }}
              className={`relative min-w-0 flex-1 cursor-pointer truncate whitespace-nowrap rounded-full px-2 py-2 text-[12.5px] font-semibold transition-colors ${
                ativo ? 'text-white' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              {ativo && (
                <motion.span
                  layoutId="aba-lista-pessoas"
                  className="absolute inset-0 rounded-full bg-primary-500"
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                />
              )}
              <span className="relative">
                {aba.rotulo}{' '}
                <NumeroAnimado valor={`(${aba.contagem})`} />
              </span>
            </button>
          );
        })}
      </div>

      {/* Filtro de participação: só quando a mesma lista tem mais de uma */}
      {!vendeIngressos && mostrarAbasParticipacao && (
        <div className="-mx-4 mt-2 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden">
          <div className="flex gap-1.5 whitespace-nowrap">
            <button
              type="button"
              onClick={() => aoMudarParticipacao('todas')}
              className={`shrink-0 cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-semibold transition-colors ${
                participacao === 'todas'
                  ? 'bg-secondary-500 text-white'
                  : 'border border-secondary-200 bg-white text-foreground-700 hover:bg-background-100'
              }`}
            >
              Todas
            </button>
            {participacoes.map((p) => (
              <button
                key={p.modalidade_id}
                type="button"
                onClick={() => aoMudarParticipacao(String(p.modalidade_id))}
                className={`shrink-0 cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-semibold transition-colors ${
                  participacao === String(p.modalidade_id)
                    ? 'bg-secondary-500 text-white'
                    : 'border border-secondary-200 bg-white text-foreground-700 hover:bg-background-100'
                }`}
              >
                {p.modalidade}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Células-resumo por grupo (2×2 compacta): o painel completo fica na tela "Vagas e suplentes". */}
      {temVagas && ocultarPainelVagas && gruposComVagas.length > 0 && (
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          {gruposComVagas.map((g) => (
            <ResumoGrupo
              key={chaveGrupo(g.modalidade_id, g.opcao_id)}
              g={g}
              aoTocar={aoAbrirVagas}
            />
          ))}
        </div>
      )}

      {/* Modo seleção (imprimir fichas em lote) */}
      {modoSelecao && !vendeIngressos && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-primary-200 bg-primary-50 px-3 py-2">
          <label className="flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-foreground-800">
            <input
              type="checkbox"
              checked={todosMarcados}
              onChange={marcarTodasDaAba}
              className="h-4 w-4 cursor-pointer accent-primary-600"
            />
            Todas desta lista ({filtrados.length})
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={imprimirFichasSelecionadas}
              disabled={selecionados.size === 0}
              className="flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-3 text-[12px] font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-500"
            >
              <i className="ri-printer-line text-sm" />
              Imprimir ({selecionados.size})
            </button>
            <button
              type="button"
              onClick={() => {
                setModoSelecao(false);
                setSelecionados(new Set());
              }}
              className="flex h-9 cursor-pointer items-center whitespace-nowrap rounded-full border border-background-300 bg-white px-3 text-[12px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
            >
              Sair
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="mt-4">
        {inscritos.length === 0 ? (
          vendeIngressos ? (
            <div className="rounded-3xl bg-white p-10 text-center shadow-[0_6px_18px_rgba(20,25,60,.06)]">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
                <i className="ri-ticket-2-line text-2xl" aria-hidden />
              </span>
              <p className="mt-3 text-[16px] font-semibold text-foreground-950">
                Nenhum ingresso vendido ainda
              </p>
              <p className="mt-1 text-[14px] text-foreground-600">
                Mande o link do evento no WhatsApp da comunidade.
              </p>
              <button
                type="button"
                onClick={() => navigate(`/gerenciar/${eventoId}?divulgar=1`)}
                className="mt-5 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Ver o link de divulgação
              </button>
            </div>
          ) : (
            <div className="rounded-lg border border-secondary-200 bg-white p-10 text-center">
              <p className="text-[15px] text-foreground-800">
                Ainda não há {rotuloParticipacoes.toLowerCase()}.
              </p>
              <DicaPrimeiraVez id="painel.pessoas.vazio" eventoId={eventoId} className="mt-5 text-left" />
            </div>
          )
        ) : filtrados.length === 0 ? (
          <div className="rounded-lg border border-secondary-200 bg-white p-10 text-center">
            <p className="text-[15px] text-foreground-800">
              {temVagas && abaLista !== 'vaga' && !busca.trim() && !participacaoAtiva
                ? abaLista === 'suplente'
                  ? 'Ninguém na fila de suplentes.'
                  : 'Nenhuma inscrição cancelada.'
                : !temVagas && abaSimples === 'cancelada' && !busca.trim() && !participacaoAtiva
                  ? 'Nenhuma inscrição cancelada.'
                  : 'Nenhum resultado com esses filtros.'}
            </p>
            <button
              type="button"
              onClick={() => {
                setBusca('');
                setFiltroRapido('todos');
                setParticipacao('todas');
                setAbaSimples('ativas');
                if (temVagas) setAbaLista('vaga');
                setSelecionados(new Set());
                setPagina(1);
              }}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
            >
              Limpar filtros
            </button>
          </div>
        ) : (
          <TrocaFade chave={`${temVagas ? abaLista : abaSimples}-${filtroRapido}`}>
            {vendeIngressos ? (
              <ListaCompradores
                compradores={paginados}
                eventoNome={eventoNome}
                aoAbrir={setSelecionado}
                aoGerarConvite={setConvite}
              />
            ) : temVagas && abaLista === 'suplente' ? (
              <ListaSuplentes
                itens={paginados}
                fila={filaSuplentes}
                totais={totaisPorGrupo}
                detalheMap={detalheMap}
                saldoMap={saldoMap}
                temFoto={temFoto}
                fotoUrlMap={fotoUrlMap}
                mostrarParticipacao={!papel && !participacaoAtiva}
                equipeMap={papel === 'equipe' ? equipeMap : undefined}
                filaMap={papel === 'campista' ? filaMap : undefined}
                vagasMap={temVagas ? infoVagas : undefined}
                selecionados={selecionados}
                aoAlternarSelecao={modoSelecao ? alternarSelecao : undefined}
                aoAbrir={setSelecionado}
              />
            ) : (
              <>
                <div className="hidden md:block">
                  <TabelaInscritos
                    inscritos={paginados}
                    saldoMap={saldoMap}
                    temFoto={temFoto}
                    fotoUrlMap={fotoUrlMap}
                    mostrarParticipacao={!papel && !participacaoAtiva}
                    equipeMap={papel === 'equipe' ? equipeMap : undefined}
                    filaMap={papel === 'campista' ? filaMap : undefined}
                    vagasMap={temVagas ? infoVagas : undefined}
                    selecionados={selecionados}
                    aoAlternarSelecao={modoSelecao ? alternarSelecao : undefined}
                    saindoId={saindoId}
                    aoAbrir={setSelecionado}
                  />
                </div>
                <div className="space-y-3 md:hidden">
                  {paginados.map((i) => (
                    <LinhaQueSai key={i.inscricao_id} ativo={saindoId === i.inscricao_id}>
                      <CartaoInscritoMobile
                        inscrito={i}
                        saldo={saldoMap.get(i.inscricao_id) ?? null}
                        temFoto={temFoto}
                        fotoUrlMap={fotoUrlMap}
                        mostrarParticipacao={!papel && !participacaoAtiva}
                        equipeMap={papel === 'equipe' ? equipeMap : undefined}
                        filaMap={papel === 'campista' ? filaMap : undefined}
                        vagasMap={temVagas ? infoVagas : undefined}
                        selecionados={selecionados}
                        aoAlternarSelecao={modoSelecao ? alternarSelecao : undefined}
                        aoAbrir={setSelecionado}
                      />
                    </LinhaQueSai>
                  ))}
                </div>
              </>
            )}

            <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
              <span className="text-sm text-secondary-500">
                Mostrando {inicio + 1}–{Math.min(inicio + POR_PAGINA, filtrados.length)} de{' '}
                {filtrados.length} {rotuloParticipacoes.toLowerCase()}
              </span>
              {totalPaginas > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                    disabled={paginaSegura === 1}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-secondary-300 bg-white text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-left-s-line text-lg" />
                  </button>
                  <span className="px-2 text-sm text-foreground-800">
                    Página {paginaSegura} de {totalPaginas}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                    disabled={paginaSegura === totalPaginas}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-secondary-300 bg-white text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-right-s-line text-lg" />
                  </button>
                </div>
              )}
            </div>
          </TrocaFade>
        )}
      </div>

      {aviso && !aviso.acaoRotulo && (
        <Aviso aberto texto={aviso.texto} tipo="neutro" aoFechar={() => setAviso(null)} />
      )}

      {aviso?.acaoRotulo && aviso.inscricaoId && (
        <div className="fixed inset-x-3 bottom-4 z-[70] mx-auto flex max-w-md items-center gap-3 rounded-2xl bg-foreground-900 px-4 py-3 text-white shadow-lg">
          <p className="min-w-0 flex-1 text-[13px] font-medium leading-snug">{aviso.texto}</p>
          {aviso.acaoRotulo && aviso.inscricaoId && (
            <button
              type="button"
              onClick={() => {
                aoDarVagaDoPainel(aviso.inscricaoId as number, aviso.texto);
                setAviso(null);
              }}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-[12px] font-bold text-foreground-900"
            >
              {aviso.acaoRotulo}
            </button>
          )}          <button
            type="button"
            onClick={() => setAviso(null)}
            aria-label="Fechar aviso"
            className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-white/70 hover:text-white"
          >
            <i className="ri-close-line" aria-hidden />
          </button>
        </div>
      )}

      {selecionado && (
        <PainelInscricao
          inscrito={{
            ...selecionado,
            ...(detalheMap.get(selecionado.inscricao_id) ?? {}),
          }}
          usuarioId={usuarioId}
          eventoId={eventoId}
          eventoNome={eventoNome}
          eventoInicio={eventoInicio}
          eventoLocal={eventoLocal ?? null}
          eventoCapa={eventoCapa}
          vendeIngressos={vendeIngressos}
          vagas={contextoVagas(selecionado)}
          aoAcaoVagas={(a) => {
            setAviso(a);
            agendarSaida(selecionado?.inscricao_id ?? null);
          }}
          aoFechar={() => setSelecionado(null)}
          aoMudouStatus={aoMudouStatus}
          aoMudouFinanceiro={aoMudouFinanceiro}
        />
      )}

      {convite && (
        <FolhaConvite
          inscrito={convite}
          eventoNome={eventoNome}
          eventoInicio={eventoInicio}
          eventoLocal={eventoLocal ?? null}
          aoFechar={() => setConvite(null)}
        />
      )}

      <Sheet aberto={acoesAbertas} titulo="Ações da lista" aoFechar={() => setAcoesAbertas(false)}>
        <div className="space-y-1 pb-2">
          {mostrarFicha && (
            <LinhaAcao
              icone="ri-filter-3-line"
              titulo="Filtrar pela ficha"
              descricao="Perguntas e respostas do formulário"
              aoTocar={() => {
                setAcoesAbertas(false);
                setModalFiltro(true);
              }}
            />
          )}
          <LinhaAcao
            icone="ri-money-dollar-circle-line"
            titulo="Só quem falta pagar"
            descricao="Quem ainda tem saldo em aberto"
            ativo={filtroRapido === 'falta'}
            aoTocar={() => {
              setFiltroRapido((v) => (v === 'falta' ? 'todos' : 'falta'));
              setSelecionados(new Set());
              setPagina(1);
              setAcoesAbertas(false);
            }}
          />
          <LinhaAcao
            icone="ri-checkbox-circle-line"
            titulo={vendeIngressos ? 'Só pagos' : 'Só confirmadas'}
            descricao="Pagamento confirmado"
            ativo={filtroRapido === 'pagos'}
            aoTocar={() => {
              setFiltroRapido((v) => (v === 'pagos' ? 'todos' : 'pagos'));
              setSelecionados(new Set());
              setPagina(1);
              setAcoesAbertas(false);
            }}
          />
          {mostrarFicha && (
            <LinhaAcao
              icone="ri-file-list-3-line"
              titulo="Relatórios"
              descricao="Gráficos e listas da ficha"
              aoTocar={() => {
                setAcoesAbertas(false);
                setModalRelatorio(true);
              }}
            />
          )}
          <LinhaAcao
            icone="ri-file-excel-2-line"
            titulo="Baixar planilha"
            descricao={baixando ? 'Gerando o arquivo...' : 'CSV com quem está nesta lista'}
            desabilitado={baixando || filtrados.length === 0}
            aoTocar={() => {
              setAcoesAbertas(false);
              baixarPlanilha();
            }}
          />
          <LinhaAcao
            icone="ri-printer-line"
            titulo="Imprimir lista"
            descricao="Lista simples para levar na mão"
            desabilitado={filtrados.length === 0}
            aoTocar={() => {
              setAcoesAbertas(false);
              imprimirLista();
            }}
          />
          {!vendeIngressos && (
            <LinhaAcao
              icone="ri-checkbox-multiple-line"
              titulo="Selecionar várias"
              descricao="Marcar pessoas e imprimir as fichas"
              ativo={modoSelecao}
              desabilitado={filtrados.length === 0}
              aoTocar={() => {
                setModoSelecao((v) => {
                  if (v) setSelecionados(new Set());
                  return !v;
                });
                setAcoesAbertas(false);
              }}
            />
          )}
        </div>
      </Sheet>

      {modalFiltro && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModalFiltro(false)} aria-hidden />
          <div className="relative z-10 flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-xl sm:rounded-3xl">
            <div className="flex items-center justify-between gap-3 border-b border-secondary-100 px-5 py-4">
              <h2 className="text-lg font-bold text-foreground-950">Filtrar pela ficha</h2>
              <button
                type="button"
                onClick={() => setModalFiltro(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
                aria-label="Fechar"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-5">
              <AbaFiltrar
                eventoId={eventoId}
                eventoNome={eventoNome}
                totalInscritos={inscritos.length}
                inscricaoIds={inscricaoIdsParticipacao}
              />
            </div>
          </div>
        </div>
      )}

      {modalRelatorio && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModalRelatorio(false)} aria-hidden />
          <div className="relative z-10 flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-xl sm:rounded-3xl">
            <div className="flex items-center justify-between gap-3 border-b border-secondary-100 px-5 py-4">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-foreground-950">Relatórios da ficha</h2>
                <p className="mt-0.5 truncate text-[13px] text-foreground-600">
                  Filtro: {resumoFiltro}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalRelatorio(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
                aria-label="Fechar"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-5">
              <AbaRelatorios
                eventoId={eventoId}
                eventoNome={eventoNome}
                inscricaoIds={
                  papel
                    ? temFiltroAtivo
                      ? inscricaoIdsFiltrados
                      : inscritos.map((i) => i.inscricao_id)
                    : temFiltroAtivo
                      ? inscricaoIdsFiltrados
                      : null
                }
                camisetaIds={temFiltroAtivo ? inscricaoIdsFiltrados : null}
                resumoFiltro={resumoFiltro}
                papel={papel}
                situacaoCamisetas={situacaoCamisetas}
                travarSituacaoCamisetas={temFiltroAtivo}
                rotuloSituacaoCamisetas={rotuloSituacaoCamisetas}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}